import { Controller } from "@hotwired/stimulus";
import * as i18n from "src/decidim/refactor/moved/i18n";
const COUNT_KEY = "%count%";
const SR_ANNOUNCE_THRESHOLD_RATIO = 0.1;
const SR_ANNOUNCE_EVERY_THRESHOLD = 10;
export default class extends Controller {
  connect() {
    var _a;
    const targetSelector = this.element.dataset.remainingCharacters;
    this.target = targetSelector ? document.querySelector(targetSelector) : null;
    this.minCharacters = parseInt(this.element.getAttribute("minlength"), 10);
    this.maxCharacters = parseInt(this.element.getAttribute("maxlength"), 10);
    this.describeByCounter = this.element.type !== "hidden" && typeof this.element.getAttribute("aria-describedby") === "undefined";
    if (this.maxCharacters > 10) {
      if (this.maxCharacters > 100) {
        this.announceThreshold = Math.floor(this.maxCharacters * SR_ANNOUNCE_THRESHOLD_RATIO);
      } else {
        this.announceThreshold = 10;
      }
      this.announceEveryThreshold = SR_ANNOUNCE_EVERY_THRESHOLD;
    } else {
      this.announceThreshold = 1;
      this.announceEveryThreshold = 1;
    }
    let targetId = (_a = this.target) == null ? void 0 : _a.getAttribute("id");
    if (typeof targetId === "undefined") {
      if (this.element.getAttribute("id") && this.element.getAttribute("id").length > 0) {
        targetId = `${this.element.getAttribute("id")}_characters`;
      } else {
        targetId = `characters_${Math.random().toString(36).substr(2, 9)}`;
      }
    }
    if (this.target) {
      this.target.setAttribute("id", targetId);
    } else {
      const span = document.createElement("span");
      span.id = targetId;
      span.className = "input-character-counter__text";
      this.target = span;
      this.container = document.createElement("span");
      this.container.className = "input-character-counter__container";
      this.container.appendChild(span);
      if (this.element.parentElement.classList.contains("editor")) {
        this.element.parentElement.appendChild(this.container);
      } else {
        this.element.after(this.container);
      }
    }
    if (this.target && (this.maxCharacters > 0 || this.minCharacters > 0)) {
      const screenReaderId = `${targetId}_sr`;
      this.srTarget = document.getElementById(screenReaderId);
      if (!this.srTarget) {
        this.srTarget = document.createElement("span");
        this.srTarget.setAttribute("role", "status");
        this.srTarget.id = screenReaderId;
        this.srTarget.className = "sr-only remaining-character-count-sr";
        this.srTarget.setAttribute("aria-hidden", "true");
        this.target.parentNode.insertBefore(this.srTarget, this.target);
      }
      this.target.setAttribute("aria-hidden", "true");
      this.userInput = this.element;
      if (this.element.parentElement.classList.contains("editor")) {
        setTimeout(() => {
          const editorContainer = this.element.parentElement.querySelector(".editor-container");
          if (editorContainer) {
            const proseMirror = editorContainer.querySelector(".ProseMirror");
            if (proseMirror) {
              this.editor = proseMirror.editor;
              this.userInput = proseMirror;
            }
          }
          this.initializeCounter();
        });
      } else {
        this.initializeCounter();
      }
    }
  }
  initializeCounter() {
    this.updateInputLength();
    this.previousInputLength = this.inputLength;
    this.bindEvents();
    this.setDescribedBy(true);
  }
  setDescribedBy(active) {
    if (!this.describeByCounter) {
      return;
    }
    if (active) {
      this.userInput.setAttribute("aria-describedby", this.srTarget.getAttribute("id"));
    } else {
      this.userInput.removeAttribute("aria-describedby");
    }
  }
  bindEvents() {
    if (this.editor) {
      this.editor.on("update", () => this.handleInput());
    } else {
      this.userInput.addEventListener("input", () => this.handleInput());
    }
    this.userInput.addEventListener("keyup", () => this.updateStatus());
    this.userInput.addEventListener("focus", () => this.updateScreenReaderStatus());
    this.userInput.addEventListener("blur", () => {
      this.updateScreenReaderStatus();
      this.setDescribedBy(true);
    });
    if (this.userInput) {
      this.userInput.addEventListener("emoji.added", () => this.updateStatus());
    }
    this.updateStatus();
    this.updateScreenReaderStatus();
  }
  getInputLength() {
    return this.inputLength;
  }
  updateInputLength() {
    this.previousInputLength = this.inputLength;
    if (this.editor) {
      this.inputLength = this.editor.storage.characterCount.characters();
    } else {
      this.inputLength = this.element.value.length;
    }
  }
  handleInput() {
    this.updateInputLength();
    this.checkScreenReaderUpdate();
    this.setDescribedBy(false);
  }
  /**
   * This compares the current inputLength to the previous value and decides
   * whether the user is currently adding or deleting characters from the view.
   *
   * @returns {String} The input direction either "ins" for insert or "del" for
   *   delete.
   */
  getInputDirection() {
    if (this.inputLength < this.previousInputLength) {
      return "del";
    }
    return "ins";
  }
  getScreenReaderLength() {
    const currentLength = this.getInputLength();
    if (this.maxCharacters < 10) {
      return currentLength;
    } else if (this.maxCharacters - currentLength <= this.announceEveryThreshold) {
      return currentLength;
    }
    const srLength = currentLength - currentLength % this.announceThreshold;
    if (this.getInputDirection() === "del") {
      if (srLength === currentLength) {
        return srLength;
      } else if (this.maxCharacters - srLength === this.announceThreshold) {
        return this.announcedAt || currentLength;
      } else if (srLength < currentLength) {
        return srLength + this.announceThreshold;
      }
    } else if (srLength < this.announcedAt) {
      return this.announcedAt;
    }
    return srLength;
  }
  getMessages(currentLength = null) {
    const showMessages = [];
    let inputLength = currentLength;
    if (inputLength === null) {
      inputLength = this.getInputLength();
    }
    if (this.minCharacters > 0) {
      let message = i18n.getMessages("characterCounter.charactersAtLeast.other");
      if (this.minCharacters === 1) {
        message = i18n.getMessages("characterCounter.charactersAtLeast.one");
      }
      showMessages.push(message.replace(COUNT_KEY, this.minCharacters));
    }
    if (this.maxCharacters > 0) {
      const remaining = this.maxCharacters - inputLength;
      let message = i18n.getMessages("characterCounter.charactersLeft.other");
      if (remaining === 1) {
        message = i18n.getMessages("characterCounter.charactersLeft.one");
      }
      this.userInput.dispatchEvent(
        new CustomEvent("characterCounter", { detail: { remaining } })
      );
      showMessages.push(message.replace(COUNT_KEY, remaining));
    }
    return showMessages;
  }
  disconnect() {
    if (this.container) {
      this.container.remove();
    }
  }
  updateStatus() {
    this.target.textContent = this.getMessages().join(", ");
  }
  checkScreenReaderUpdate() {
    if (this.maxCharacters < 1) {
      return;
    }
    const currentLength = this.getScreenReaderLength();
    if (currentLength === this.announcedAt) {
      return;
    }
    this.announcedAt = currentLength;
    this.updateScreenReaderStatus(currentLength);
  }
  updateScreenReaderStatus(currentLength = null) {
    this.srTarget.textContent = this.getMessages(currentLength).join(", ");
  }
}
